// Deck rules: 60 cards + 1 Emperor, per-card copy limits, export/import text.
import { type CardDb, type CardDef, RENAMED, isEmperor } from './cards.ts';

export const MAIN_DECK_SIZE = 60;
export const EMPEROR_COUNT = 1;

/** card number -> number of copies */
export type Deck = Map<number, number>;

export function deckStats(deck: Deck, db: CardDb) {
  let emperors = 0;
  let main = 0;
  for (const [num, copies] of deck) {
    const card = db.byNumber.get(num);
    if (!card) continue;
    if (isEmperor(card)) emperors += copies;
    else main += copies;
  }
  return { emperors, main };
}

/** Returns why the card can't be added, or null if it can. */
export function whyCantAdd(deck: Deck, card: CardDef, db: CardDb): string | null {
  const { emperors, main } = deckStats(deck, db);
  const have = deck.get(card.number) ?? 0;
  if (card.spawnOnly) return `${card.name} can’t go in a deck: it is only summoned by other cards (Spawn menu).`;
  if (isEmperor(card)) {
    if (emperors >= EMPEROR_COUNT) return 'You can’t add that — your deck is full. It already has an Emperor.';
    return null;
  }
  if (main >= MAIN_DECK_SIZE) return `You can’t add that — your deck is full (${MAIN_DECK_SIZE}/${MAIN_DECK_SIZE} cards).`;
  if (have >= card.limit) {
    const copies = have === 1 ? '1 copy' : `${have} copies`;
    return `You can’t add that — you already have ${copies} of ${card.name} in your deck.`;
  }
  return null;
}

export function addCard(deck: Deck, num: number) {
  deck.set(num, (deck.get(num) ?? 0) + 1);
}

export function removeCard(deck: Deck, num: number) {
  const have = deck.get(num) ?? 0;
  if (have <= 1) deck.delete(num);
  else deck.set(num, have - 1);
}

/** Every problem that stops the deck from being saved (empty list = legal). */
export function deckProblems(deck: Deck, db: CardDb): string[] {
  const problems: string[] = [];
  const { emperors, main } = deckStats(deck, db);
  if (emperors !== EMPEROR_COUNT) problems.push(`Your deck needs exactly 1 Emperor (it has ${emperors}).`);
  if (main !== MAIN_DECK_SIZE) problems.push(`Your deck needs exactly ${MAIN_DECK_SIZE} cards besides the Emperor (it has ${main}).`);
  for (const [num, copies] of deck) {
    const card = db.byNumber.get(num);
    if (!card) problems.push(`Card #${num} isn’t in the card list anymore.`);
    else if (card.spawnOnly) problems.push(`${card.name} can’t be in a deck: it is only summoned by other cards.`);
    else if (copies > card.limit) problems.push(`${card.name} allows ${card.limit} copies, but the deck has ${copies}.`);
  }
  return problems;
}

/** Deck sorted by set number, as [card, copies] pairs. */
export function deckEntries(deck: Deck, db: CardDb): [CardDef, number][] {
  return [...deck.entries()]
    .map(([num, copies]) => [db.byNumber.get(num), copies] as const)
    .filter((e): e is readonly [CardDef, number] => !!e[0])
    .map(([c, n]) => [c, n] as [CardDef, number])
    .sort((a, b) => a[0].number - b[0].number);
}

export const DEFAULT_DECK_NAME = 'Untitled Deck';

export function exportDeck(deck: Deck, db: CardDb, name = DEFAULT_DECK_NAME): string {
  const entries = deckEntries(deck, db);
  const emperor = entries.filter(([c]) => isEmperor(c));
  const main = entries.filter(([c]) => !isEmperor(c));
  const line = ([c, n]: [CardDef, number]) => `${n}x #${c.number} ${c.name}`;
  const { main: mainCount } = deckStats(deck, db);
  return [
    '# Deck export — paste this into Import Deck to load it',
    `NAME: ${name.trim() || DEFAULT_DECK_NAME}`,
    'EMPEROR',
    ...emperor.map(line),
    `MAIN DECK (${mainCount})`,
    ...main.map(line),
  ].join('\n');
}

/**
 * Reads text made by exportDeck. Card lines look like "3x #12 Card 12" (only the
 * number counts, so renaming a card later doesn't break old exports).
 * "NAME: ..." sets the deck name; it's null when the text has none.
 */
export function importDeck(text: string, db: CardDb): { deck: Deck; name: string | null; warnings: string[] } {
  const deck: Deck = new Map();
  const warnings: string[] = [];
  let name: string | null = null;
  for (const raw of text.split(/\r?\n/)) {
    const named = raw.match(/^\s*name\s*:\s*(.+?)\s*$/i);
    if (named) {
      name = named[1].slice(0, 40);
      continue;
    }
    const m = raw.match(/^\s*(\d+)\s*x\s*(?:[A-Za-z&]+\s*)?#?\s*(\d+)\s*(.*)$/i);
    if (!m) continue;
    const copies = Number(m[1]);
    // the card's name, when the line has one, decides (a card renumbered or renamed since the deck was exported)
    const written = m[3].trim().toLowerCase();
    const wanted = written ? (Object.entries(RENAMED).find(([old]) => old.toLowerCase() === written)?.[1] ?? m[3].trim()).toLowerCase() : '';
    const byName = wanted ? db.list.find((c) => c.name.toLowerCase() === wanted) : undefined;
    const num = byName?.number ?? Number(m[2]);
    if (!db.byNumber.has(num) || db.byNumber.get(num)!.spawnOnly) {
      warnings.push(`Skipped card #${num} — it isn’t in the card list.`);
      continue;
    }
    deck.set(num, (deck.get(num) ?? 0) + copies);
  }
  if (deck.size === 0) warnings.push('No cards were found in that text. Lines should look like “3x #12 Card 12”.');
  return { deck, name, warnings };
}

/** One entry per physical card, Emperor included. */
export function deckToList(deck: Deck): number[] {
  const list: number[] = [];
  for (const [num, copies] of deck) for (let i = 0; i < copies; i++) list.push(num);
  return list;
}
